/* eslint-disable eslint-js/no-restricted-syntax */
import {
  Button,
  ButtonGroup,
  closeDialog,
  CustomRequestOption,
  Form,
  FormField,
  HelperText,
  IconButton,
  isFile,
  Label,
  openDialog,
  showNotification,
  Upload,
  UploadState,
} from 'platform/components';
import {
  Box,
  Heading,
  Hide,
  HStack,
  Icon,
  Link,
  Show,
  Space,
  Text,
  VStack,
} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';
import {z} from 'zod';

import {useState} from 'react';

import {always, isNil} from 'ramda';

import {useUploadFileMutation} from '@dms/api/base';
import {
  ContextTarget,
  useAddTaskFileMutation,
  useGetChecklistQuery,
  useRemoveTaskFileMutation,
  useUpdateTaskNoteMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {openFile, useBoolean} from 'shared/web';

import {getFileIcon} from '../../../utils/getFileIcon';
import {handleApiError} from '../../../utils/handleApiError';
import {EntityDocumentsSelectGrid} from '../../EntityDocumentsSelectGrid/EntityDocumentsSelectGrid';
import {TaskCompletedImage} from './TaskCompletedImage';

const updateTaskSchema = z.object({
  note: z.string().optional(),
});

type UpdateTaskFormValue = z.infer<typeof updateTaskSchema>;

interface EditTaskFile {
  id: string;
  name: string;
  contentType: string;
  url: string;
}

interface EditTaskDialogProps {
  dialogId: string;
  taskName: string;
  recordId: string;
  resourceId: string;
  checklistId: string;
  taskId: string;
  note: string;
  files: EditTaskFile[];
  fileRequired: boolean;
  highlightFileUpload?: boolean;
  contextTarget?: ContextTarget;
  contextId?: string;
}

export function EditTaskDialog(props: EditTaskDialogProps) {
  const [uploadFile, uploadFileQuery] = useUploadFileMutation();

  const [attachNote, attachNoteQuery] = useUpdateTaskNoteMutation();
  const [addTaskFile] = useAddTaskFileMutation();
  const [removeTaskFile] = useRemoveTaskFileMutation();

  const [isUploading, setIsUploading, stopIsUploading] = useBoolean();
  const [hasUploadError, showUploadError, hideUploadError] = useBoolean();
  const [removingFileIds, setRemovingFileIds] = useState<ReadonlySet<string>>(new Set());

  const checklistQuery = useGetChecklistQuery({
    recordId: props.recordId,
    resourceId: props.resourceId,
  });

  const liveFiles: EditTaskFile[] =
    checklistQuery.data?.tasks
      .find((task) => task.id === props.taskId)
      ?.files.map((file) => ({
        id: file.id,
        name: file.originalFilename,
        contentType: file.contentType ?? '',
        url: file.fileUri,
      })) ?? props.files;

  const uploadState = match<[boolean, boolean], UploadState | undefined>([
    hasUploadError,
    isUploading,
  ])
    .with([true, Pattern.boolean], always(UploadState.Error))
    .with([Pattern.boolean, true], always(UploadState.Uploading))
    .with([false, false], always(UploadState.Success))
    .otherwise(always(undefined));

  const handleSubmit = async (formData: UpdateTaskFormValue) => {
    await attachNote({
      resourceId: props.resourceId,
      recordId: props.recordId,
      checklistId: props.checklistId,
      taskId: props.taskId,
      body: {
        note: formData.note || '',
      },
    })
      .unwrap()
      .catch(handleApiError);

    return closeDialog(props.dialogId);
  };

  const attachFileToTask = (fileId: string) =>
    addTaskFile({
      resourceId: props.resourceId,
      recordId: props.recordId,
      checklistId: props.checklistId,
      taskId: props.taskId,
      body: {fileId},
    })
      .unwrap()
      .catch(handleApiError);

  const handleUploadFromDisk = async ({file, onSuccess, onError}: CustomRequestOption) => {
    if (!isFile(file)) {
      return;
    }

    hideUploadError();
    setIsUploading();

    try {
      const data = await uploadFile({file}).unwrap();
      await attachFileToTask(data.fileId);
      onSuccess?.(data);
    } catch (err) {
      showUploadError();
      handleApiError(err as Error);
      onError?.(err as Error);
    } finally {
      stopIsUploading();
    }
  };

  const handleUploadFromSystem = () => {
    if (!props.contextTarget || !props.contextId) {
      showNotification.error();
      return;
    }

    openDialog(
      <EntityDocumentsSelectGrid
        recordId={props.contextId}
        contextTarget={props.contextTarget}
        onSelect={(files) => {
          files.forEach((file) => attachFileToTask(file.fileId));
        }}
      />,
      {size: 'large', title: i18n.t('entity.document.labels.addDocumentsFromSystem')}
    );
  };

  const handleRemoveFile = (fileId: string) => {
    setRemovingFileIds((prev) => {
      const next = new Set(prev);
      next.add(fileId);
      return next;
    });

    removeTaskFile({
      resourceId: props.resourceId,
      recordId: props.recordId,
      checklistId: props.checklistId,
      taskId: props.taskId,
      fileId,
    })
      .unwrap()
      .catch(handleApiError)
      .finally(() => {
        setRemovingFileIds((prev) => {
          const next = new Set(prev);
          next.delete(fileId);
          return next;
        });
      });
  };

  const hasFiles = liveFiles.length > 0;
  const fileUploadErrorMessage =
    props.highlightFileUpload && !hasFiles ? i18n.t('errors.field_is_required') : undefined;

  return (
    <Form<UpdateTaskFormValue>
      values={{
        note: props.note || '',
      }}
      onSubmit={handleSubmit}
      experimentalZodSchema={updateTaskSchema}
    >
      {(control) => (
        <VStack spacing={4} width="100%">
          <Box padding={6}>
            <VStack spacing={4} align="center" width="100%">
              <TaskCompletedImage />
              <Heading size={2}>{props.taskName}</Heading>
              <Box maxWidth={165}>
                <Text color="secondary" size="small" align="center">
                  {i18n.t('entity.checklist.labels.editTaskDescription')}
                </Text>
              </Box>
              <Box width="100%">
                <FormField
                  data-testid={testIds.vehicles.checklists('note')}
                  control={control}
                  name="note"
                  type="textarea"
                  label={i18n.t('general.labels.note')}
                  isResizable
                />
              </Box>
              <Box width="100%">
                <Label isRequired={props.fileRequired}>
                  {i18n.t('entity.document.actions.uploadDocument')}
                </Label>
                <VStack spacing={2}>
                  {liveFiles.map((file) => (
                    <HStack key={file.id} align="center" spacing={3} width="100%">
                      <Icon value={getFileIcon(file.contentType)} size={8} />
                      <Link
                        data-testid={testIds.vehicles.checklists(`document-${file.id}`)}
                        size="small"
                        title={file.name}
                        onClick={() => openFile(file.url)}
                      />
                      <Space fillAvailable />
                      <IconButton
                        severity="danger"
                        icon="action/delete"
                        isDisabled={removingFileIds.has(file.id)}
                        onClick={() => handleRemoveFile(file.id)}
                        size="small"
                      />
                    </HStack>
                  ))}
                </VStack>
                <Show when={hasFiles}>
                  <Space vertical={2} />
                </Show>
                <HStack spacing={2}>
                  {/**
                   * TODO: Add accept prop to Upload component
                   * https://carvago.atlassian.net/browse/T20-81708
                   */
                  /* @ts-expect-error */}
                  <Upload
                    data-testid={testIds.vehicles.checklists('uploadDocument')}
                    type="button"
                    buttonVariant="secondary"
                    isMultiple
                    uploadState={uploadState}
                    customRequest={handleUploadFromDisk}
                  />

                  <Hide when={isNil(props.contextTarget) || isNil(props.contextId)}>
                    <Button
                      variant="secondary"
                      onClick={handleUploadFromSystem}
                      leftIcon="content/add_circle"
                      title={i18n.t('entity.document.labels.addDocumentsFromSystem')}
                    />
                  </Hide>
                </HStack>

                <HelperText errorMessage={fileUploadErrorMessage} />
              </Box>
            </VStack>
          </Box>
          <ButtonGroup align="right">
            <Button
              variant="secondary"
              title={i18n.t('general.labels.discard')}
              onClick={() => closeDialog(props.dialogId)}
              isDisabled={attachNoteQuery.isLoading}
              data-testid={testIds.vehicles.checklists('discard')}
            />
            <Button
              data-testid={testIds.vehicles.checklists('submit')}
              title={i18n.t('general.actions.save')}
              type="submit"
              isLoading={attachNoteQuery.isLoading}
              isDisabled={uploadFileQuery.isLoading}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
